import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Phone, MapPin, Clock } from 'lucide-react';
import { isoServices } from '../mockData';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ContactPage = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [isHeaderCompact, setIsHeaderCompact] = useState(false);
  const [formData, setFormData] = useState({
    inquiryType: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    standard: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setIsHeaderCompact((prev) => {
        if (prev) {
          return y > 95;
        }
        return y > 145;
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.inquiryType) {
      toast({ title: 'Please select an inquiry type.', variant: 'destructive', duration: 4000 });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inquiry_type: formData.inquiryType,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company,
          standard: formData.standard || null,
          message: formData.message || null,
        }),
      });
      if (!res.ok) throw new Error('Server error');
      toast({
        title: `${formData.inquiryType} Received!`,
        description: "Thank you for reaching out. We'll get back to you within 24 hours.",
        duration: 5000,
      });
      setFormData({ inquiryType: '', name: '', email: '', phone: '', company: '', standard: '', message: '' });
    } catch {
      toast({
        title: 'Submission failed',
        description: 'Please try again or call us directly at (251) 654 7929.',
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Compact fixed bar shown only after scrolling past hero */}
      <div
        className="fixed left-0 right-0 bg-gradient-to-r from-[#003f6d] to-[#004a85] text-white z-40 border-b border-[#00365d]"
        style={{
          top: '90px',
          height: '72px',
          opacity: isHeaderCompact ? 1 : 0,
          pointerEvents: 'none',
          transform: isHeaderCompact ? 'translateY(0)' : 'translateY(-8px)',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
        }}
      >
        <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <h1 className="font-bold text-[34px] leading-none">Contact Us</h1>
            <div className="w-14 h-1 bg-[#d4af37] mt-1"></div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#003f6d] to-[#004a85] text-white min-h-[280px] flex items-center justify-center">
        <div className="max-w-7xl w-full px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-bold text-[52px] leading-tight">Contact Us</h1>
          <div className="w-20 h-1 bg-[#d4af37] mt-2 mb-4 mx-auto"></div>
          <p className="text-lg text-gray-200 max-w-3xl mx-auto">
            Get in touch with our certification experts. We're here to help you on your certification journey.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">

                    {/* Inquiry Type — first field */}
                    <div>
                      <Label htmlFor="inquiryType">Inquiry Type *</Label>
                      <Select
                        value={formData.inquiryType}
                        onValueChange={handleSelectChange('inquiryType')}
                        required
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select inquiry type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Quote">Quote — request pricing or a proposal</SelectItem>
                          <SelectItem value="Feedback">Feedback — share comments or questions</SelectItem>
                          <SelectItem value="Complaint/Appeals">Complaint/Appeals — submit a complaint or appeal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-2"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="mt-2"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2"
                          placeholder="(123) 456-7890"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">Company Name *</Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          required
                          value={formData.company}
                          onChange={handleChange}
                          className="mt-2"
                          placeholder="Company Inc."
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="standard">ISO Standard of Interest</Label>
                      <Select
                        value={formData.standard}
                        onValueChange={handleSelectChange('standard')}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a standard (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {isoServices.map((service) => (
                            <SelectItem key={service.id} value={service.code}>
                              {service.code} - {service.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="multiple">Multiple Standards</SelectItem>
                          <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="mt-2"
                        rows={6}
                        placeholder="Tell us about your organization and certification needs..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#003f6d] hover:bg-[#002a4d] text-white py-6 text-lg disabled:opacity-60"
                    >
                      {submitting ? 'Submitting…' : 'Submit'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="border-t-4 border-[#003f6d]">
                <CardContent className="p-6">
                  <Phone className="w-8 h-8 text-[#003f6d] mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
                  <p className="text-gray-600 mb-2">Mon-Fri: 8:00 AM - 6:00 PM EST</p>
                  <a href="tel:+12516547929" className="text-[#003f6d] font-semibold text-lg hover:underline">
                    (251) 654 7929
                  </a>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-[#c1272d]">
                <CardContent className="p-6">
                  <MapPin className="w-8 h-8 text-[#c1272d] mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Visit Us</h3>
                  <p className="text-gray-600">
                    5500 Cherokee Ave. Ste 100<br />
                    Alexandria, VA 22312
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#003f6d] text-white">
                <CardContent className="p-6">
                  <Clock className="w-8 h-8 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Business Hours</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span>8:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span>9:00 AM - 2:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;
